import { useEffect, useId, useState, type FormEvent } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Carte, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import {
  useReglerDelaiReclamations,
  useReglerResponsableReclamations,
  type AntenneReclamations,
  type VueReglagesReclamations,
} from '../../lib/useReclamations';
import { CLASSE_BOUTON_ENREGISTRER, CLASSE_CHAMP_NOMBRE, mentionReglage, messageErreur } from './commun';

const CLASSE_CHOIX =
  'min-h-[44px] w-full min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20 sm:w-72';

/**
 * Réclamations (TF-482) : le responsable des réclamations de chaque antenne,
 * à qui toute réclamation déposée est assignée d'office, et le délai de
 * traitement visé, au-delà duquel une réclamation est « hors délai ».
 */
export function OngletReclamations({ reclamations }: { reclamations: VueReglagesReclamations }): JSX.Element {
  return (
    <>
      <CarteDelai delai={reclamations.delai} />
      <Carte titre="Responsable des réclamations par antenne">
        <p className="px-4 py-3 text-sm text-codialis-gray sm:px-6">
          Une réclamation déposée lui est assignée d’office, et il en est prévenu par e-mail. Sans responsable, elle
          reste à assigner et la direction de l’antenne en est prévenue. Il est choisi au pôle pédagogique, à
          l’administratif ou à la direction : les autres pôles n’ont pas le registre.
        </p>
        {reclamations.antennes.map((antenne) => (
          <LigneAntenne key={antenne.code} antenne={antenne} />
        ))}
      </Carte>
    </>
  );
}

function CarteDelai({ delai }: { delai: VueReglagesReclamations['delai'] }): JSX.Element {
  const toast = useToast();
  const regler = useReglerDelaiReclamations();
  const id = useId();
  const [jours, setJours] = useState(String(delai.jours));
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => setJours(String(delai.jours)), [delai.jours]);

  const valeur = Number(jours);
  const valide = Number.isInteger(valeur) && valeur >= delai.min && valeur <= delai.max;

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (!valide) return;
    setErreur(null);
    regler.mutate(valeur, {
      onSuccess: () => toast('Délai visé enregistré'),
      onError: (refus) => setErreur(messageErreur(refus, 'Le délai n’a pas pu être enregistré.')),
    });
  };

  return (
    <Carte titre="Délai de traitement visé">
      <form onSubmit={envoyer} className="space-y-3 px-4 py-4 sm:px-6">
        <p className="text-sm text-codialis-gray">
          Jours civils entre la réception d’une réclamation et sa clôture. Au-delà, le registre la signale « hors
          délai ». {mentionReglage(delai)}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label htmlFor={`${id}-jours`} className="text-xs font-semibold text-codialis-navy">
            Délai visé, en jours
            <input
              id={`${id}-jours`}
              type="number"
              inputMode="numeric"
              min={delai.min}
              max={delai.max}
              value={jours}
              onChange={(evenement) => setJours(evenement.target.value)}
              className={`mt-1 block ${CLASSE_CHAMP_NOMBRE}`}
            />
          </label>
          <button
            type="submit"
            className={CLASSE_BOUTON_ENREGISTRER}
            disabled={!valide || valeur === delai.jours || regler.isPending}
          >
            {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
        {!valide && jours !== '' && (
          <p className="text-xs text-red-700">
            De {delai.min} à {delai.max} jours.
          </p>
        )}
        {erreur && <MessageErreur message={erreur} />}
      </form>
    </Carte>
  );
}

function LigneAntenne({ antenne }: { antenne: AntenneReclamations }): JSX.Element {
  const toast = useToast();
  const regler = useReglerResponsableReclamations();
  const id = useId();
  const actuel = antenne.responsable?.id ?? '';
  const [choix, setChoix] = useState(actuel);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => setChoix(actuel), [actuel]);

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(
      { code: antenne.code, responsableId: choix || null },
      {
        onSuccess: () =>
          toast(choix ? `Responsable des réclamations de ${antenne.code} enregistré` : `Responsable de ${antenne.code} retiré`),
        onError: (refus) => setErreur(messageErreur(refus, 'Le responsable n’a pas pu être enregistré.')),
      },
    );
  };

  return (
    <form onSubmit={envoyer} className="space-y-2 px-4 py-3 sm:px-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-codialis-navy">
            {antenne.nom} · {antenne.code}
          </div>
          <div className="text-[11px] text-codialis-gray">
            {antenne.responsable
              ? `${antenne.responsable.nom} · ${antenne.responsable.fonction}. ${mentionReglage({ parDefaut: false, ...antenne })}`
              : 'Aucun responsable : chaque réclamation reste à assigner.'}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label htmlFor={`${id}-responsable`} className="w-full min-w-0 text-xs font-semibold text-codialis-navy sm:w-auto">
            Responsable
            <select
              id={`${id}-responsable`}
              value={choix}
              onChange={(evenement) => setChoix(evenement.target.value)}
              className={`mt-1 block ${CLASSE_CHOIX}`}
            >
              <option value="">Aucun responsable</option>
              {antenne.candidats.map((candidat) => (
                <option key={candidat.id} value={candidat.id}>
                  {candidat.nom} · {candidat.fonction}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className={CLASSE_BOUTON_ENREGISTRER} disabled={choix === actuel || regler.isPending}>
            {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
      {antenne.responsableInvalide && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <TriangleAlert className="mt-px h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0">
            {antenne.responsableInvalide} était désigné(e), mais son compte est désactivé ou n’est plus rattaché à
            l’antenne : choisissez un autre responsable.
          </span>
        </p>
      )}
      {erreur && <MessageErreur message={erreur} />}
    </form>
  );
}
